import { Alert, Button, TextField } from '@cartly/ui';
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { api } from '../../api/client';
import { useSubmit } from '../../app/hooks';
import { useSession } from '../../app/session';
import { AuthLayout } from '../../layouts/AuthLayout';

export function MfaChallengePage() {
  const { refresh, logout } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const [useRecovery, setUseRecovery] = useState(false);
  const [value, setValue] = useState('');
  const { pending, error, run } = useSubmit();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const body = useRecovery ? { recoveryCode: value.trim() } : { code: value.trim() };
    const result = await run(() => api.post<{ state: string }>('/auth/mfa/verify', body));
    if (!result) return;
    await refresh();
    navigate(from, { replace: true });
  };

  return (
    <AuthLayout
      title="Two-factor verification"
      subtitle={
        useRecovery
          ? 'Enter one of the recovery codes you saved when you turned on two-factor authentication.'
          : 'Enter the 6-digit code from your authenticator app.'
      }
      footer={
        <button
          type="button"
          className="link-button"
          onClick={() => void logout().then(() => navigate('/login'))}
        >
          Sign in as someone else
        </button>
      }
    >
      <form className="stack" onSubmit={onSubmit} noValidate>
        {error && <Alert tone="critical">{error}</Alert>}
        {useRecovery ? (
          <TextField
            label="Recovery code"
            autoComplete="one-time-code"
            placeholder="xxxxx-xxxxx"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
          />
        ) : (
          <TextField
            label="Authentication code"
            className="input--code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, ''))}
            autoFocus
          />
        )}
        <Button type="submit" variant="primary" block loading={pending}>
          Verify
        </Button>
        <button
          type="button"
          className="link-button"
          onClick={() => {
            setUseRecovery((v) => !v);
            setValue('');
          }}
        >
          {useRecovery ? 'Use authenticator code instead' : 'Use a recovery code'}
        </button>
      </form>
    </AuthLayout>
  );
}
